import { useMemo, useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import moment from 'moment';
import { Box, Typography, styled, InputAdornment, Divider, useTheme } from '@mui/material';
import Grid from '../components/Grid';
import DateRangePicker from '../components/DateRangePicker';
import StatusBadge from '../components/StatusBadge';
import InputField from '../components/InputField';
import {
  FilterArrow,
  FilterButton,
  FilterButtonCaption,
  FilterButtonLabel,
  FilterMenu,
  FilterToolbar,
  GridHeader,
  SearchInputWrapper,
  SortMenuItem,
  SortMenuSection,
  SortMenuSectionHeader,
  StatusMenu,
  StatusMenuItem,
  ThemedSearchIcon,
} from '../components/FilterToolbarStyles';
import { formatDate } from '../helpers/date';
import api from '../services/api';
import { useDocumentsList } from '../hooks/documentsList';
import {
  DriversCheckboxCell,
  DriversCheckboxHeader,
  DriversFooterRow,
} from '../components/drivers';
import Dialog, {
  DialogBody,
  DialogButtonRow,
  DialogCancelButton,
  DialogDestructiveButton,
} from '../components/Dialog';
import SvgIcon from '../components/SvgIcon';
import { ReactComponent as UploadedFileIcon } from '../assets/icons/40/file_uploaded.svg';
import { getThemeColors } from '../helpers/themeStyles';
import { FILE_STATUS_VARIANTS } from '../constants/file';
import { getFileStatusLabel } from '../helpers/status';
import { THEME_MODES } from '../theme';
import { pathDriver } from '../constants/routes';
import VerifyDocumentAmountDialog from '../components/VerifyDocumentAmountDialog';
import FilePreviewDialog from '../components/FilePreviewDialog';
import { showGlobalToast } from '../services/toastBus';

const SORT_OPTIONS = {
  alphabetical: {
    options: [
      { value: 'a-z', label: 'A à Z' },
      { value: 'z-a', label: 'Z à A' },
    ],
  },
  date: {
    options: [
      { value: 'newest', label: 'Récent → Ancien' },
      { value: 'oldest', label: 'Ancien → Récent' },
    ],
  },
};

const Body = styled(Box)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  height: '100%',
  margin: '0px 32px 32px 0px',
  overflow: 'hidden',
  borderRadius: 28,
  border: `1px solid ${theme.palette.divider}`,
  backgroundColor:
    theme.palette.mode === THEME_MODES.DARK ? '#232323' : theme.palette.background.default,
}));

const getRowId = (params) => String(params.data?.id);
const isFullWidthRow = ({ rowNode }) => rowNode?.data?.id === 'footer';

const DocumentsScreen = () => {
  const theme = useTheme();
  const colors = getThemeColors(theme);
  const navigate = useNavigate();
  const { data: documentRows, loading, refetch } = useDocumentsList();
  const [checkedIds, setCheckedIds] = useState(new Set());
  const [searchQuery, setSearchQuery] = useState('');
  const [sortAnchorEl, setSortAnchorEl] = useState(null);
  const [statusAnchorEl, setStatusAnchorEl] = useState(null);
  const [statusRow, setStatusRow] = useState(null);
  const [verifyDialogOpen, setVerifyDialogOpen] = useState(false);
  const [pendingVerifyRow, setPendingVerifyRow] = useState(null);
  const [verifySaving, setVerifySaving] = useState(false);
  const [previewFile, setPreviewFile] = useState(null);
  const sortOpen = Boolean(sortAnchorEl);
  const statusMenuOpen = Boolean(statusAnchorEl);
  const [sortConfig, setSortConfig] = useState({ category: 'date', value: 'newest' });
  const [dateFilterRange, setDateFilterRange] = useState({ start: null, end: null });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  const filteredData = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    const { start, end } = dateFilterRange;

    return documentRows.filter((row) => {
      const matchesSearch =
        !query ||
        (row.name || '').toLowerCase().includes(query) ||
        (row.email || '').toLowerCase().includes(query) ||
        (row.documentName || '').toLowerCase().includes(query) ||
        String(row.userId || '').includes(query);

      const rowMoment = moment(row.date);
      const matchesDate =
        !row.date ||
        (!start && !end) ||
        (start &&
          end &&
          rowMoment.isSameOrAfter(start, 'day') &&
          rowMoment.isSameOrBefore(end, 'day')) ||
        (start && !end && rowMoment.isSameOrAfter(start, 'day')) ||
        (!start && end && rowMoment.isSameOrBefore(end, 'day'));

      return matchesSearch && matchesDate;
    });
  }, [documentRows, searchQuery, dateFilterRange]);

  const sortedData = useMemo(() => {
    const { category, value } = sortConfig;
    const arr = [...filteredData];

    if (category === 'alphabetical') {
      arr.sort((a, b) => {
        const nameA = (a.name || '').trim().toLowerCase();
        const nameB = (b.name || '').trim().toLowerCase();
        const cmp = nameA.localeCompare(nameB);
        return value === 'z-a' ? -cmp : cmp;
      });
    } else if (category === 'date') {
      arr.sort((a, b) => {
        const dateA = new Date(a.date || 0).getTime();
        const dateB = new Date(b.date || 0).getTime();
        return value === 'oldest' ? dateA - dateB : dateB - dateA;
      });
    }
    return arr;
  }, [filteredData, sortConfig]);

  const rows = useMemo(
    () =>
      sortedData.map((row) => ({
        ...row,
        isChecked: checkedIds.has(row.id),
      })),
    [sortedData, checkedIds]
  );

  const handleToggle = useCallback((id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const handleToggleAll = useCallback(() => {
    setCheckedIds((prev) => (prev.size ? new Set() : new Set(rows.map((r) => r.id))));
  }, [rows]);

  const handleSortOpen = (e) => setSortAnchorEl(e.currentTarget);
  const handleSortClose = () => setSortAnchorEl(null);

  const handleSortSelect = useCallback((category, value) => {
    setSortConfig({ category, value });
    setSortAnchorEl(null);
  }, []);

  const isAllChecked = checkedIds.size > 0 && rows.length === checkedIds.size;

  const handleStatusClick = useCallback((event, data) => {
    setStatusAnchorEl(event.currentTarget);
    setStatusRow(data);
  }, []);

  const handleStatusMenuClose = useCallback(() => {
    setStatusAnchorEl(null);
    setStatusRow(null);
  }, []);

  const handleStatusSelect = useCallback(
    async (newStatus) => {
      if (!statusRow?.id) return;

      if (newStatus === FILE_STATUS_VARIANTS.VERIFIED) {
        setPendingVerifyRow({ ...statusRow });
        handleStatusMenuClose();
        setVerifyDialogOpen(true);
        return;
      }

      const response = await api.patch(`/files/${statusRow.id}/status`, {
        status: newStatus,
      });
      if (!response?.ok) return;

      refetch();
      handleStatusMenuClose();
    },
    [statusRow, refetch, handleStatusMenuClose]
  );

  const handleVerifyDialogClose = useCallback(() => {
    setVerifyDialogOpen(false);
    setPendingVerifyRow(null);
  }, []);

  const handleDocumentPreview = useCallback((data) => {
    const url = data?.url || data?.downloadUrl;
    if (!url) return;
    setPreviewFile({
      url,
      downloadUrl: data.downloadUrl || data.url,
      name: data.documentName,
      fileName: data.fileName,
    });
  }, []);

  const handleVerifySave = useCallback(
    async ({ amount, percentage, date }) => {
      if (!pendingVerifyRow?.id) return;

      if (date == null) {
        showGlobalToast('Veuillez sélectionner une date.', 'error');
        return;
      }
      const dateObj = date instanceof Date ? date : new Date(date);
      if (Number.isNaN(dateObj.getTime())) {
        showGlobalToast('Veuillez sélectionner une date valide.', 'error');
        return;
      }

      const parsedAmount = parseFloat(String(amount).replace(',', '.'));
      if (Number.isNaN(parsedAmount) || parsedAmount < 0) {
        showGlobalToast('Veuillez saisir un montant valide.', 'error');
        return;
      }

      const parsedPct = parseFloat(String(percentage).replace(',', '.'));
      const pct = Number.isNaN(parsedPct) ? 20 : parsedPct;

      setVerifySaving(true);
      try {
        const response = await api.patch(`/files/${pendingVerifyRow.id}/status`, {
          status: FILE_STATUS_VARIANTS.VERIFIED,
          amount: parsedAmount,
          percentage: pct,
          date: dateObj.toISOString(),
        });
        if (!response?.ok) return;

        refetch();
        handleVerifyDialogClose();
      } finally {
        setVerifySaving(false);
      }
    },
    [pendingVerifyRow, refetch, handleVerifyDialogClose]
  );

  const handleOpenBulkDelete = useCallback(() => {
    if (!checkedIds.size) return;
    setDeleteDialogOpen(true);
  }, [checkedIds.size]);

  const handleBulkDeleteConfirm = useCallback(async () => {
    const fileIds = Array.from(checkedIds);
    if (!fileIds.length) return;

    const response = await api.delete('/files/user/bulk', { data: { ids: fileIds } });
    if (!response?.ok) return;

    refetch();
    setCheckedIds(new Set());
    setDeleteDialogOpen(false);
  }, [checkedIds, refetch]);

  const fullWidthCellRenderer = useCallback(
    (params) => <DriversFooterRow data={params.data} onRemove={handleOpenBulkDelete} />,
    [handleOpenBulkDelete]
  );

  const pinnedBottomRowData = useMemo(
    () => (checkedIds.size ? [{ id: 'footer', size: checkedIds.size }] : undefined),
    [checkedIds.size]
  );

  const columnDefs = useMemo(
    () => [
      {
        headerComponent: DriversCheckboxHeader,
        cellRenderer: DriversCheckboxCell,
        width: 63,
        maxWidth: 63,
        minWidth: 63,
        pinned: 'left',
      },
      {
        field: 'name',
        headerName: 'Nom',
        flex: 1,
        minWidth: 232,
        headerAlign: 'left',
        cellClass: 'ag-cell-left',
        cellRenderer: ({ data }) => (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '4px',
              cursor: data.userId ? 'pointer' : 'default',
              '&:hover': data.userId ? { opacity: 0.8 } : {},
            }}
            onClick={() => data.userId && navigate(pathDriver(data.userId))}
          >
            <Typography sx={{ fontSize: 16, fontWeight: 500 }}>{data.name}</Typography>
            <Typography sx={{ fontSize: 14, fontWeight: 500, color: 'text.secondary' }}>
              {data.email}
            </Typography>
          </Box>
        ),
      },
      {
        field: 'documentName',
        headerName: 'Document',
        flex: 1,
        minWidth: 200,
        headerAlign: 'left',
        cellClass: 'ag-cell-left',
        cellRenderer: ({ data }) => {
          const canOpen = Boolean(data.url || data.downloadUrl);
          return (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
                minWidth: 0,
                cursor: canOpen ? 'pointer' : 'default',
                ...(canOpen ? { '&:hover': { opacity: 0.85 } } : {}),
              }}
              onClick={() => canOpen && handleDocumentPreview(data)}
            >
              <SvgIcon icon={UploadedFileIcon} color={colors.iconOnSurface} size={28} />
              <Typography
                sx={{
                  fontSize: 14,
                  fontWeight: 500,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {data.documentName}
              </Typography>
            </Box>
          );
        },
      },
      {
        field: 'date',
        headerName: 'Date',
        initialWidth: 170,
        valueFormatter: ({ data }) =>
          data.date ? formatDate(data.date, { format: 'medium' }) : '',
      },
      {
        field: 'status',
        headerName: 'Statut',
        initialWidth: 140,
        cellRenderer: ({ data }) => (
          <StatusBadge
            active
            variant={data.status}
            sx={{ cursor: 'pointer' }}
            onClick={(e) => handleStatusClick(e, data)}
          >
            {getFileStatusLabel(data.status)}
          </StatusBadge>
        ),
      },
      {
        field: 'amount',
        headerName: 'Montant (€)',
        initialWidth: 120,
        valueFormatter: ({ data }) =>
          data.status === FILE_STATUS_VARIANTS.VERIFIED && data.amount != null
            ? String(data.amount)
            : '',
        headerAlign: 'right',
        cellClass: 'ag-cell-right',
      },
    ],
    [colors.iconOnSurface, handleDocumentPreview, handleStatusClick, navigate]
  );

  const defaultColDef = useMemo(
    () => ({
      initialWidth: 170,
      cellRendererParams: {
        onToggle: handleToggle,
      },
      headerComponentParams: {
        isAllChecked,
        onToggleAll: handleToggleAll,
      },
    }),
    [handleToggle, isAllChecked, handleToggleAll]
  );

  return (
    <Body>
      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <GridHeader>
          <Typography sx={{ fontSize: 22, fontWeight: 500, letterSpacing: 0.06 }}>
            Documents
          </Typography>
        </GridHeader>

        <FilterToolbar>
          <Box sx={{ display: 'flex', gap: 3 }}>
            <SearchInputWrapper>
              <InputField
                fullWidth
                placeholder="Rechercher"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  shrink: false,
                  startAdornment: (
                    <InputAdornment position="start">
                      <ThemedSearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{
                  '& .MuiOutlinedInput-input': {
                    padding: '10px 0px',
                  },
                  '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-input': {
                    padding: '10px 0px',
                  },
                }}
              />
            </SearchInputWrapper>

            <DateRangePicker
              value={dateFilterRange}
              onChange={setDateFilterRange}
              label="Date"
              placeholder="Toutes"
            />
          </Box>

          <FilterButton
            $open={sortOpen}
            onClick={handleSortOpen}
            aria-haspopup="true"
            aria-expanded={sortOpen ? 'true' : undefined}
          >
            <Box
              sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0 }}
            >
              <FilterButtonCaption>Trier par</FilterButtonCaption>
              <FilterButtonLabel>
                {SORT_OPTIONS[sortConfig.category].options.find((o) => o.value === sortConfig.value)
                  ?.label ?? 'Récent → Ancien'}
              </FilterButtonLabel>
            </Box>
            <FilterArrow open={sortOpen} />
          </FilterButton>

          <FilterMenu
            anchorEl={sortAnchorEl}
            open={sortOpen}
            onClose={(_, reason) => {
              if (reason === 'backdropClick' || reason === 'escapeKeyDown') handleSortClose();
            }}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          >
            <SortMenuSection>
              <SortMenuSectionHeader>Alphabétique</SortMenuSectionHeader>
              {SORT_OPTIONS.alphabetical.options.map(({ value, label }) => (
                <SortMenuItem
                  key={value}
                  $selected={sortConfig.category === 'alphabetical' && sortConfig.value === value}
                  onClick={() => handleSortSelect('alphabetical', value)}
                  disableRipple
                >
                  {label}
                </SortMenuItem>
              ))}
            </SortMenuSection>
            <Divider sx={{ borderColor: 'divider' }} />
            <SortMenuSection>
              <SortMenuSectionHeader>Date</SortMenuSectionHeader>
              {SORT_OPTIONS.date.options.map(({ value, label }) => (
                <SortMenuItem
                  key={value}
                  $selected={sortConfig.category === 'date' && sortConfig.value === value}
                  onClick={() => handleSortSelect('date', value)}
                  disableRipple
                >
                  {label}
                </SortMenuItem>
              ))}
            </SortMenuSection>
          </FilterMenu>

          {!!statusRow && (
            <StatusMenu
              anchorEl={statusAnchorEl}
              open={statusMenuOpen}
              onClose={(_, reason) => {
                if (reason === 'backdropClick' || reason === 'escapeKeyDown')
                  handleStatusMenuClose();
              }}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              {[
                FILE_STATUS_VARIANTS.PENDING,
                FILE_STATUS_VARIANTS.VERIFIED,
                FILE_STATUS_VARIANTS.REJECTED,
              ]
                .filter((status) => status !== statusRow?.status)
                .map((status) => (
                  <StatusMenuItem
                    key={status}
                    onClick={() => handleStatusSelect(status)}
                    disableRipple
                  >
                    <StatusBadge variant={status}>{getFileStatusLabel(status)}</StatusBadge>
                  </StatusMenuItem>
                ))}
            </StatusMenu>
          )}
        </FilterToolbar>

        <Box sx={{ flex: 1, minHeight: 0 }}>
          <Grid
            loading={loading}
            animateRows
            suppressScrollOnNewData
            getRowId={getRowId}
            rowData={rows}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            fullWidthCellRenderer={fullWidthCellRenderer}
            pinnedBottomRowData={pinnedBottomRowData}
            isFullWidthRow={isFullWidthRow}
          />
        </Box>

        <Dialog
          open={deleteDialogOpen}
          closable={false}
          onClose={() => setDeleteDialogOpen(false)}
          title="SUPPRIMER LES DOCUMENTS ?"
          slotProps={{
            title: {
              sx: {
                marginBottom: '12px',
                textAlign: 'center',
              },
            },
          }}
          PaperProps={{ sx: { width: 400 } }}
        >
          <DialogBody>
            {checkedIds.size > 1
              ? `Êtes-vous sûr de vouloir supprimer ces ${checkedIds.size} documents ?`
              : 'Êtes-vous sûr de vouloir supprimer ce document ?'}
          </DialogBody>
          <DialogButtonRow>
            <DialogCancelButton variant="contained" onClick={() => setDeleteDialogOpen(false)}>
              ANNULER
            </DialogCancelButton>
            <DialogDestructiveButton variant="contained" onClick={handleBulkDeleteConfirm}>
              SUPPRIMER
            </DialogDestructiveButton>
          </DialogButtonRow>
        </Dialog>

        <VerifyDocumentAmountDialog
          open={verifyDialogOpen}
          onClose={handleVerifyDialogClose}
          onSave={handleVerifySave}
          saving={verifySaving}
        />

        <FilePreviewDialog
          open={!!previewFile}
          onClose={() => setPreviewFile(null)}
          file={previewFile}
        />
      </Box>
    </Body>
  );
};

export default DocumentsScreen;
